import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { Clock, Globe, MapPin } from '@phosphor-icons/react';

export default function TimezonePage() {
  const { config, selectedEntity, entities } = useTenant();
  const { isSuperAdmin } = useAuth();

  // Get current time in selected entity timezone
  const getCurrentTime = (tz) => {
    try {
      return new Date().toLocaleString('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return new Date().toLocaleTimeString();
    }
  };

  const getCurrentDate = (tz) => {
    try {
      return new Date().toLocaleDateString('en-US', {
        timeZone: tz,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return new Date().toLocaleDateString();
    }
  };

  return (
    <div className="space-y-6" data-testid="timezone-page">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold text-[#1C1917] tracking-tight"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          Timezone
        </h1>
        <p 
          className="text-[#78716C] mt-1"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          View and manage entity timezones
        </p>
      </div>

      {/* Current Entity Time */}
      {selectedEntity && (
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-8">
          <div className="text-center">
            <p 
              className="text-xs text-[#78716C] uppercase tracking-wider mb-2"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {selectedEntity.entity_name} - {selectedEntity.timezone}
            </p>
            <p 
              className="text-5xl font-light text-[#0A2540] mb-2"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {getCurrentTime(selectedEntity.timezone)}
            </p>
            <p 
              className="text-lg text-[#78716C]"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {getCurrentDate(selectedEntity.timezone)}
            </p>
          </div>
        </div>
      )}

      {/* All Entities Timezones (Super Admin) */}
      {isSuperAdmin && (
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6">
          <h2 
            className="text-lg font-semibold text-[#0A2540] tracking-tight mb-6"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Entity Timezones
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entities.map((entity) => (
              <div 
                key={entity.id}
                className="p-4 border border-[#EDE6D8] rounded-lg hover:bg-[#FCFAF6] transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p 
                      className="font-medium text-[#1C1917]"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    >
                      {entity.entity_name}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-[#78716C] mt-1">
                      <Globe size={12} />
                      <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {entity.country_code}
                      </span>
                    </div>
                  </div>
                  <Clock size={20} className="text-[#0A2540]" />
                </div>
                
                <div className="bg-[#E6EEE7] p-3 rounded-lg">
                  <p 
                    className="text-xl font-semibold text-[#0A2540]"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {getCurrentTime(entity.timezone)}
                  </p>
                  <p 
                    className="text-xs text-[#78716C] mt-1"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {entity.timezone}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Available Timezones */}
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6">
        <h2 
          className="text-lg font-semibold text-[#0A2540] tracking-tight mb-6"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          Available Timezones
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {config.timezones.map((tz) => (
            <div 
              key={tz.value}
              className="flex items-center justify-between p-3 bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg"
            >
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-[#78716C]" />
                <span 
                  className="text-sm text-[#1C1917]"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {tz.label}
                </span>
              </div>
              <span 
                className="text-sm font-medium text-[#0A2540]"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {getCurrentTime(tz.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
